import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

interface ShipmentData {
  billof_lading?: string;
  brl_reference?: string;
  shipper_name?: string;
  shipper_address?: string;
  consignee_name?: string;
  consignee_address?: string;
  notify_party_name?: string;
  notify_party_address?: string;
  delivery_instructions?: string;
  pre_carriage_by?: string;
  place_of_receipt?: string;
  vessel?: string;
  voyage?: string;
  port_of_loading?: string;
  port_of_discharge?: string;
  place_of_delivery?: string;
  marks_and_numbers?: string;
  ncm_hs?: string;
  package_description?: string;
  gross_weight?: number | string;
  measurement?: number | string;
  freight_details?: string;
  prepaid?: boolean;
  collect?: boolean;
  freight_payable_at?: string;
  place_date_of_issue?: string;
  number_of_original_bl?: number | string;
  bl_signature?: string;
}

interface ContainerData {
  container_number?: string;
  container_type?: string;
  seal?: string;
  tare?: string | number;
  gross_weight?: string | number;
  net_weight?: string | number;
  cbm?: string | number;
  qty?: string | number;
  vgm?: string | number;
}

const loadImageAsBase64 = async (imagePath: string): Promise<string> => {
  try {
    const response = await fetch(imagePath);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Error loading image:', error);
    return '';
  }
};

export const generateBillOfLadingPDF = async (
  shipmentData: ShipmentData,
  containers: ContainerData[] = [],
  options: { returnBase64?: boolean } = {}
): Promise<string | void> => {
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'pt',
    format: 'a4',
  });

  const pageWidth = 595.28;
  const margin = 30;
  const contentWidth = pageWidth - 2 * margin;

  doc.setFont('helvetica', 'normal');

  // ── HEADER ──────────────────────────────────────────────────────────────────
  try {
    const logoBase64 = await loadImageAsBase64('/BRL Logo Blue.png');
    if (logoBase64) doc.addImage(logoBase64, 'PNG', margin, 20, 100, 50);
  } catch {}

  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('HOUSE BILL OF LADING', pageWidth - margin, 30, { align: 'right' });
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text('FCL / FCL', pageWidth - margin, 45, { align: 'right' });
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  const blNumber = shipmentData.brl_reference || shipmentData.billof_lading || '';
  doc.text(blNumber, pageWidth - margin, 65, { align: 'right' });

  // ── ROW 1: Shipper | Consignee ───────────────────────────────────────────────
  let currentY = 85;
  const col1X = margin;
  const col2X = pageWidth / 2;
  const halfWidth = contentWidth / 2;
  const boxHeight = 90;

  doc.setLineWidth(0.5);

  const drawBox = (x: number, y: number, w: number, h: number) => doc.rect(x, y, w, h);
  const labelText = (text: string, x: number, y: number) => {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.text(text, x, y);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
  };

  // Shipper
  drawBox(col1X, currentY, halfWidth, boxHeight);
  labelText('Shipper:', col1X + 5, currentY + 11);
  if (shipmentData.shipper_name) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.text(shipmentData.shipper_name, col1X + 5, currentY + 22);
    doc.setFont('helvetica', 'normal');
  }
  if (shipmentData.shipper_address) {
    const lines = doc.splitTextToSize(shipmentData.shipper_address, halfWidth - 10);
    doc.setFontSize(7);
    doc.text(lines, col1X + 5, currentY + 33);
  }

  // Consignee
  drawBox(col2X, currentY, halfWidth, boxHeight);
  labelText('Consignee:', col2X + 5, currentY + 11);
  if (shipmentData.consignee_name) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.text(shipmentData.consignee_name, col2X + 5, currentY + 22);
    doc.setFont('helvetica', 'normal');
  }
  if (shipmentData.consignee_address) {
    const lines = doc.splitTextToSize(shipmentData.consignee_address, halfWidth - 10);
    doc.setFontSize(7);
    doc.text(lines, col2X + 5, currentY + 33);
  }

  currentY += boxHeight;

  // ── ROW 2: Notify Party | For delivery ──────────────────────────────────────
  drawBox(col1X, currentY, halfWidth, boxHeight);
  labelText('Notify Party:', col1X + 5, currentY + 11);
  if (shipmentData.notify_party_name) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.text(shipmentData.notify_party_name, col1X + 5, currentY + 22);
    doc.setFont('helvetica', 'normal');
  }
  if (shipmentData.notify_party_address) {
    const lines = doc.splitTextToSize(shipmentData.notify_party_address, halfWidth - 10);
    doc.setFontSize(7);
    doc.text(lines, col1X + 5, currentY + 33);
  }

  drawBox(col2X, currentY, halfWidth, boxHeight);
  labelText('For delivery of the goods apply to:', col2X + 5, currentY + 11);
  if (shipmentData.delivery_instructions) {
    const lines = doc.splitTextToSize(shipmentData.delivery_instructions, halfWidth - 10);
    doc.setFontSize(7);
    doc.text(lines, col2X + 5, currentY + 22);
  }

  currentY += boxHeight;

  // ── ROW 3: Pre-carriage | Place of receipt | Place of delivery ───────────────
  const smallH = 28;
  const colW = contentWidth / 3;

  drawBox(col1X, currentY, colW, smallH);
  labelText('Pre-carriage by', col1X + 5, currentY + 9);
  doc.setFontSize(8);
  doc.text(shipmentData.pre_carriage_by || '', col1X + 5, currentY + 20);

  drawBox(col1X + colW, currentY, colW, smallH);
  labelText('Place of receipt by pre-carrier', col1X + colW + 5, currentY + 9);
  doc.setFontSize(8);
  doc.text(shipmentData.place_of_receipt || '', col1X + colW + 5, currentY + 20);

  drawBox(col1X + 2 * colW, currentY, colW, smallH);
  labelText('Place of delivery by on-carrier', col1X + 2 * colW + 5, currentY + 9);
  doc.setFontSize(8);
  doc.text(shipmentData.place_of_delivery || '', col1X + 2 * colW + 5, currentY + 20);

  currentY += smallH;

  // ── ROW 4: Port of loading | Port of discharge | Vessel / Voyage ─────────────
  drawBox(col1X, currentY, colW, smallH);
  labelText('Port of loading', col1X + 5, currentY + 9);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text(shipmentData.port_of_loading || '', col1X + 5, currentY + 21);
  doc.setFont('helvetica', 'normal');

  drawBox(col1X + colW, currentY, colW, smallH);
  labelText('Port of discharge', col1X + colW + 5, currentY + 9);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text(shipmentData.port_of_discharge || '', col1X + colW + 5, currentY + 21);
  doc.setFont('helvetica', 'normal');

  drawBox(col1X + 2 * colW, currentY, colW, smallH);
  labelText('Vessel / Voyage', col1X + 2 * colW + 5, currentY + 9);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  const vesselText = [shipmentData.vessel, shipmentData.voyage].filter(Boolean).join(' / ');
  doc.text(vesselText, col1X + 2 * colW + 5, currentY + 21);
  doc.setFont('helvetica', 'normal');

  currentY += smallH;

  // ── CARGO TABLE ──────────────────────────────────────────────────────────────
  // Single body row — no head/body separator. Labels drawn via didDrawCell.
  // Column widths sum to contentWidth (535.28): 85+65+256+65+64
  const cargoColWidths = [85, 65, 256, 65, 64];
  const cargoLabels = [
    'Marks and Nos.',
    'NCM / HS',
    'Number and kind of packages; description of goods; said to contain',
    'Gross weight',
    'Measurement',
  ];

  autoTable(doc, {
    startY: currentY + 3,
    showHead: 'never',
    body: [[
      shipmentData.marks_and_numbers || '',
      shipmentData.ncm_hs || '',
      shipmentData.package_description || '',
      shipmentData.gross_weight?.toString() || '',
      shipmentData.measurement?.toString() || '',
    ]],
    theme: 'grid',
    tableWidth: contentWidth,
    styles: {
      fontSize: 8,
      cellPadding: { top: 14, left: 3, right: 3, bottom: 3 },
      lineColor: [0, 0, 0],
      lineWidth: 0.5,
      valign: 'top',
      textColor: [0, 0, 0],
      minCellHeight: 260,
    },
    columnStyles: {
      0: { cellWidth: cargoColWidths[0] },
      1: { cellWidth: cargoColWidths[1] },
      2: { cellWidth: cargoColWidths[2] },
      3: { cellWidth: cargoColWidths[3], halign: 'right' },
      4: { cellWidth: cargoColWidths[4], halign: 'right' },
    },
    didDrawCell: (data: any) => {
      if (data.section === 'body' && data.row.index === 0) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7);
        const labelX = data.cell.x + 3;
        const labelY = data.cell.y + 9;
        const labelWidth = cargoColWidths[data.column.index] - 6;
        const wrapped = doc.splitTextToSize(cargoLabels[data.column.index], labelWidth);
        doc.text(wrapped, labelX, labelY);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
      }
    },
    margin: { left: margin, right: margin },
  });

  currentY = (doc as any).lastAutoTable?.finalY + 3;

  // ── CONTAINER TABLE ──────────────────────────────────────────────────────────
  // 8 columns summing to 535.28
  // 120 + 40 + 50 + 65 + 65 + 55 + 35 + 105.28 ≈ 535.28
  const containerRows = containers.length > 0
    ? containers.map((c) => [
        c.container_number || '',
        c.container_type || 'FCL',
        c.tare?.toString() || '',
        c.gross_weight?.toString() || '',
        c.net_weight?.toString() || '',
        c.cbm?.toString() || '',
        c.qty?.toString() || '',
        c.seal?.toString() || '',
      ])
    : [['', 'FCL', '', '', '', '', '', '']];

  autoTable(doc, {
    startY: currentY,
    head: [['CONTAINER', 'TYPE', 'TARE', 'GROSS', 'NET WEIGHT', 'CBM', 'QTY', 'SEAL']],
    body: containerRows,
    theme: 'grid',
    tableWidth: contentWidth,
    styles: {
      fontSize: 7,
      cellPadding: 3,
      lineColor: [0, 0, 0],
      lineWidth: 0.5,
      valign: 'middle',
      halign: 'center',
    },
    headStyles: {
      fillColor: [255, 255, 255],
      textColor: [0, 0, 0],
      fontStyle: 'bold',
      fontSize: 7,
      halign: 'center',
    },
    columnStyles: {
      0: { cellWidth: contentWidth / 8 },
      1: { cellWidth: contentWidth / 8 },
      2: { cellWidth: contentWidth / 8 },
      3: { cellWidth: contentWidth / 8 },
      4: { cellWidth: contentWidth / 8 },
      5: { cellWidth: contentWidth / 8 },
      6: { cellWidth: contentWidth / 8 },
      7: { cellWidth: contentWidth / 8 },
    },
    margin: { left: margin, right: margin },
  });

  currentY = (doc as any).lastAutoTable?.finalY + 3;

  // ── FOOTER ROW 1: Freight | Payment | Other Info ─────────────────────────────
  const footerH = 50;

  drawBox(col1X, currentY, colW, footerH);
  labelText('Freight details, charges, etc.', col1X + 5, currentY + 10);
  const freightText = shipmentData.freight_details || 'AS PER AGREEMENT';
  const freightLines = doc.splitTextToSize(freightText, colW - 10);
  doc.setFontSize(7);
  doc.text(freightLines, col1X + 5, currentY + 21);

  drawBox(col1X + colW, currentY, colW, footerH);
  labelText('Payment terms', col1X + colW + 5, currentY + 10);
  const paymentTerm = shipmentData.prepaid ? 'Prepaid' : 'Collect';
  doc.setFontSize(8);
  doc.text(paymentTerm, col1X + colW + 5, currentY + 21);
  labelText('Freight payable at:', col1X + colW + 5, currentY + 32);
  doc.setFontSize(8);
  doc.text(shipmentData.freight_payable_at || '', col1X + colW + 5, currentY + 43);

  drawBox(col1X + 2 * colW, currentY, colW, footerH);
  labelText('Number of original B/L:', col1X + 2 * colW + 5, currentY + 10);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text(String(shipmentData.number_of_original_bl || '3'), col1X + 2 * colW + 5, currentY + 24);
  doc.setFont('helvetica', 'normal');

  currentY += footerH;

  // ── FOOTER ROW 2: Place/date | Signature ─────────────────────────────────────
  const sigH = 55;

  drawBox(col1X, currentY, halfWidth, sigH);
  labelText('Place and date of issue:', col1X + 5, currentY + 11);
  doc.setFontSize(8);
  doc.text(shipmentData.place_date_of_issue || '', col1X + 5, currentY + 23);

  drawBox(col2X, currentY, halfWidth, sigH);
  labelText('Signature:', col2X + 5, currentY + 11);
  doc.setLineWidth(0.5);
  doc.line(col2X + 5, currentY + 44, col2X + halfWidth - 10, currentY + 44);
  if (shipmentData.bl_signature) {
    doc.setFontSize(8);
    doc.text(shipmentData.bl_signature, col2X + 5, currentY + 38);
  }

  // ── SAVE / RETURN ────────────────────────────────────────────────────────────
  if (options.returnBase64) {
    const dataUri = doc.output('datauristring');
    return dataUri.split(',')[1]; // strip data:application/pdf;base64,
  }
  const fileName = `BL_DRAFT_${new Date().getTime()}.pdf`;
  doc.save(fileName);
};
